"use client";
import { Suspense, useEffect, useState } from "react";
import FeatureCard from "@/components/marketing/FeatureCard";
import SectionContainer from "@/components/marketing/SectionContainer";
import DonationModal from "@/components/support/DonationModal";
import { CardGiftcard, MenuBook, VolunteerActivism } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useRouter, useSearchParams } from "next/navigation";

function WaysToGiveContent() {
  const [donationModalOpen, setDonationModalOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();

  // Detect payment_status query param and auto-open success modal
  useEffect(() => {
    const paymentStatus = searchParams.get("payment_status");
    if (paymentStatus === "success") {
      setShowSuccess(true);
      setDonationModalOpen(true);
    }
  }, [searchParams]);

  const handleCloseModal = () => {
    setDonationModalOpen(false);
    // Clear the query param from URL
    if (searchParams.get("payment_status")) {
      router.replace("/support", { scroll: false });
    }
  };

  // Handle inline success (when payment succeeds without redirect)
  const handleDonationSuccess = () => {
    setShowSuccess(true);
  };

  // Only reset showSuccess after the dialog animation completes
  const handleModalExited = () => {
    setShowSuccess(false);
  };

  const waysToGive = [
    {
      title: "General Fund",
      description:
        "Support daily operations, teacher salaries, and student resources. Every dollar makes a difference.",
      icon: VolunteerActivism,
      action: "Donate Now",
      onClickAction: () => {
        setShowSuccess(false);
        setDonationModalOpen(true);
      },
    },
    {
      title: "The Library Legacy",
      description:
        "Gift a book to our classroom library. Your personalized bookplate will inspire students for years to come.",
      icon: MenuBook,
      action: "Give a Book",
      href: "https://www.amazon.com/hz/wishlist/ls/35SFJ4VL9BE5M?ref=cm_sw_sm_r_un_un_Z0b3765XPKWDx",
      isExternal: true,
    },
    {
      title: "Community Wishlist",
      description:
        "Browse our Amazon Wishlist for classroom supplies, art materials, and educational tools.",
      icon: CardGiftcard,
      action: "Shop Wishlist",
      href: "https://www.amazon.com/hz/wishlist/ls/7FUKHV5AJSX?ref=cm_sw_sm_r_un_un_rclsFU4cJNuhH",
      isExternal: true,
    },
  ];

  return (
    <>
      <SectionContainer
        backgroundColor="background.default"
        py={{ xs: 8, md: 10 }}
      >
        <Box sx={{ textAlign: "center", maxWidth: "800px", mx: "auto", mb: 6 }}>
          <Typography
            component="span"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontSize: "0.875rem",
              display: "block",
              mb: 2,
            }}
          >
            Your Generosity
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              fontWeight: 600,
              color: "text.primary",
              mb: 2,
            }}
          >
            Ways to Give
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              maxWidth: "600px",
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Choose how you&apos;d like to invest in the future of education.
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {waysToGive.map((item, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
              <FeatureCard
                title={item.title}
                description={item.description}
                icon={item.icon}
                iconBackground
                action={item.action}
                actionHref={item.href}
                actionOnClick={item.onClickAction}
                actionExternal={item.isExternal}
              />
            </Grid>
          ))}
        </Grid>
      </SectionContainer>

      {/* Donation Modal */}
      <DonationModal
        open={donationModalOpen}
        onClose={handleCloseModal}
        showSuccess={showSuccess}
        onExited={handleModalExited}
        onSuccess={handleDonationSuccess}
      />
    </>
  );
}

// Fallback that renders the same UI without search params functionality
function WaysToGiveFallback() {
  const waysToGive = [
    {
      title: "General Fund",
      description:
        "Support daily operations, teacher salaries, and student resources. Every dollar makes a difference.",
      icon: VolunteerActivism,
      action: "Donate Now",
    },
    {
      title: "The Library Legacy",
      description:
        "Gift a book to our classroom library. Your personalized bookplate will inspire students for years to come.",
      icon: MenuBook,
      action: "Give a Book",
      href: "https://www.amazon.com/hz/wishlist/ls/35SFJ4VL9BE5M?ref=cm_sw_sm_r_un_un_Z0b3765XPKWDx",
      isExternal: true,
    },
    {
      title: "Community Wishlist",
      description:
        "Browse our Amazon Wishlist for classroom supplies, art materials, and educational tools.",
      icon: CardGiftcard,
      action: "Shop Wishlist",
      href: "https://www.amazon.com/hz/wishlist/ls/7FUKHV5AJSX?ref=cm_sw_sm_r_un_un_rclsFU4cJNuhH",
      isExternal: true,
    },
  ];

  return (
    <SectionContainer
      backgroundColor="background.default"
      py={{ xs: 8, md: 10 }}
    >
      <Box sx={{ textAlign: "center", maxWidth: "800px", mx: "auto", mb: 6 }}>
        <Typography
          component="span"
          sx={{
            color: "primary.main",
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontSize: "0.875rem",
            display: "block",
            mb: 2,
          }}
        >
          Your Generosity
        </Typography>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            fontWeight: 600,
            color: "text.primary",
            mb: 2,
          }}
        >
          Ways to Give
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            maxWidth: "600px",
            mx: "auto",
            lineHeight: 1.7,
          }}
        >
          Choose how you&apos;d like to invest in the future of education.
        </Typography>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {waysToGive.map((item, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
            <FeatureCard
              title={item.title}
              description={item.description}
              icon={item.icon}
              iconBackground
              action={item.action}
              actionHref={item.href}
              actionExternal={item.isExternal}
            />
          </Grid>
        ))}
      </Grid>
    </SectionContainer>
  );
}

export default function WaysToGiveSection() {
  return (
    <Suspense fallback={<WaysToGiveFallback />}>
      <WaysToGiveContent />
    </Suspense>
  );
}
